import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Form, Spinner } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { getData as getRequirements } from "../../models/RequirementsModel";
import { isResumeConverted } from "../../helpers/ResumeToCandidateHelper";

export default function CreateCandidateFromResumeModal({
  show,
  onClose,
  resume,
  clients = [],
  onConfirm,
  submitting = false,
}) {
  const { state } = useAuth();
  const user_token = state?.user_data?.user_token;

  const [selectedClient, setSelectedClient] = useState("");
  const [selectedRequirement, setSelectedRequirement] = useState("");
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);

  useEffect(() => {
    if (!show) {
      setSelectedClient("");
      setSelectedRequirement("");
      setFilteredRequirements([]);
      setLoadingRequirements(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !selectedClient || !user_token) {
      setFilteredRequirements([]);
      setSelectedRequirement("");
      return;
    }

    let cancelled = false;

    const fetchClientRequirements = async () => {
      setLoadingRequirements(true);
      setSelectedRequirement("");

      try {
        const response = await getRequirements({
          user_token,
          requirement_client_id: selectedClient,
          page: 1,
          limit: -1,
        });

        if (cancelled) return;

        const options = (response?.requirements || []).map((item) => ({
          value: item._requirement_id,
          label: item.requirement_name,
          client_id: item.requirement_client_id,
        }));

        setFilteredRequirements(options);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setFilteredRequirements([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRequirements(false);
        }
      }
    };

    fetchClientRequirements();

    return () => {
      cancelled = true;
    };
  }, [selectedClient, show, user_token]);

  const handleConfirm = () => {
    if (!selectedClient || !selectedRequirement) return;

    const client = clients.find(
      (row) => String(row.value) === String(selectedClient)
    );
    const requirement = filteredRequirements.find(
      (row) => String(row.value) === String(selectedRequirement)
    );

    onConfirm({
      clientId: selectedClient,
      clientName: client?.label || "",
      requirementId: selectedRequirement,
      requirementName: requirement?.label || "",
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Create candidate from resume</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {resume && (
          <div className="resume-convert-preview mb-3">
            <div className="fw-semibold">{resume.resume_name || "Unnamed resume"}</div>
            <div className="text-muted small">
              {[resume.resume_email, resume.resume_phone, resume.resume_location]
                .filter(Boolean)
                .join(" · ") || "Limited contact details parsed"}
            </div>
            {isResumeConverted(resume) && (
              <div className="mt-2">
                <span className="badge resume-converted-badge">Already converted</span>
              </div>
            )}
          </div>
        )}

        <div className="mb-3">
          <Form.Label>
            Client <span className="text-danger">*</span>
          </Form.Label>
          <Form.Select
            value={selectedClient}
            onChange={(event) => setSelectedClient(event.target.value)}
          >
            <option value="">Select client</option>
            {clients.map((client) => (
              <option key={client.value} value={String(client.value)}>
                {client.label}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="mb-3">
          <Form.Label>
            Requirement <span className="text-danger">*</span>
          </Form.Label>
          {loadingRequirements ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <Spinner animation="border" size="sm" />
              <span>Loading requirements…</span>
            </div>
          ) : (
            <>
              <Form.Select
                value={selectedRequirement}
                onChange={(event) => setSelectedRequirement(event.target.value)}
                disabled={!selectedClient}
              >
                <option value="">
                  {selectedClient ? "Select requirement" : "Select a client first"}
                </option>
                {filteredRequirements.map((req) => (
                  <option key={req.value} value={String(req.value)}>
                    {req.label}
                  </option>
                ))}
              </Form.Select>
              {selectedClient && !loadingRequirements && filteredRequirements.length === 0 && (
                <div className="text-muted small mt-2">
                  No requirements found for this client.
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-muted small mb-0">
          Profile fields will be prefilled on the candidate form. Review and save there —
          nothing is created until you submit.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={
            !selectedClient ||
            !selectedRequirement ||
            submitting ||
            loadingRequirements ||
            isResumeConverted(resume)
          }
        >
          {submitting ? "Opening form…" : "Continue to candidate form"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
